import {
    Alert,
    Button,
    Col,
    ColorPicker,
    ConfigProvider,
    Divider,
    Form,
    Input,
    notification as antNotification,
    Row,
    Upload,
    UploadFile,
    Checkbox,
} from "antd";
import { defaultTheme, ICustomization } from "../../redux/reducer/configuration";
import { IInstitution, updateInstitution } from "../../redux/reducer/institution";
import _, { merge } from "lodash";
import SharedLayout from "../templates/SharedLayout";
import { useCallback, useEffect, useState } from "react";
import { DeleteOutlined, UploadOutlined } from "@ant-design/icons";
import { AggregationColor } from "antd/es/color-picker/color";
import createHash from "../../utils/hash";
import TextDisplay from "./TextDisplay";
import { loremIpsum, mapConfig } from "../../utils/configuration";
import { ThemeConfigForm } from "../../interface/configuration";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../configureStore";
import { useMsal } from "@azure/msal-react";

interface ThemeConfigurationParams {
    institution: IInstitution;
}

const ThemeConfiguration = ({
    institution,
}: ThemeConfigurationParams) => {
    const { accounts } = useMsal();
    const dispatch = useDispatch<AppDispatch>();
    const [notification, contextHolder] = antNotification.useNotification();

    const loading = useSelector<RootState, boolean>(({ status }) => status.loading['institution/PATCH_INSTITUTION']);

    const [form] = Form.useForm<ThemeConfigForm>();
    useEffect(() => {
        form.setFieldsValue(mapConfig.toForm(institution.customization))
    }, [institution])

    const [fileList, setFileList] = useState<UploadFile<any>[]>([]);
    const [fileListFav, setFileListFav] = useState<UploadFile<any>[]>([]);
    const [fontLink, setFontLink] = useState<string | undefined>(institution?.customization?.fontLink);
    const [key, setKey] = useState<string>();

    const [override, setOverride] = useState<ICustomization>(institution.customization);

    const onChange = useCallback((key: string, value: any) => {
        const copied = structuredClone(override);
        _.set(copied, key, value);
        setKey(createHash(JSON.stringify(copied)));
        setOverride(copied);
    }, [setOverride, override]);

    useEffect(() => {
        const file = fileList[0]?.originFileObj;
        if (file) {
            const reader = new FileReader();
            reader.onload = () => {
                onChange('logo', reader.result as string);
            };
            reader.readAsDataURL(file);
        }
    }, [fileList]);

    useEffect(() => {
        const docLink = document.createElement('link');
        docLink.id = 'temp-font-id'
        if (fontLink) {
            docLink.href = fontLink;
            docLink.rel = 'stylesheet';
            document.head.appendChild(docLink);
        }

        return () => {
            try {
                const el = document.getElementById('temp-font-id');
                if (el) document.removeChild(el);
            } catch { /* empty */ }
        }
    }, [fontLink]);

    return (
        <>
            {contextHolder}
            <Row gutter={8}>
                <Col xs={24} sm={6}>
                    <Form
                        form={form}
                        onValuesChange={(changed, _all) => {
                            Object.keys(changed).forEach((key) => {
                                if (key === 'configuration.token.fontSize') onChange(key, changed[key] ? `${changed[key]}px` : undefined);
                                else if (changed[key] instanceof AggregationColor) onChange(key, changed[key].cleared ? undefined : changed[key].toHexString());
                                else if (['favicon', 'logo'].includes(key) && (!changed[key] || changed[key].length !== 1)) onChange(key, undefined);
                                else onChange(key, changed[key])
                            });
                        }}
                        onFinish={(v) => {
                            mapConfig.fromForm(v).then((config) => {
                                dispatch(updateInstitution({
                                    input: {
                                        id: institution.id,
                                        institutionId: institution.institutionId,
                                        customization: config,
                                    },
                                    headers: {
                                        Authorization: `Bearer ${accounts[0].idToken}`,
                                    },
                                    onSuccess: () => {
                                        notification.success({ message: 'Success' });
                                    },
                                    onFailure: (res) => {
                                        notification.error({ message: 'Failure' });
                                        console.error(res);
                                    }
                                }))
                            }).catch(console.error);
                        }}
                    >
                        <Row gutter={8}>
                            <Col span={12}>
                                <Form.Item
                                    name="logo"
                                    label="Logo"
                                    valuePropName="fileList"
                                    getValueFromEvent={e => e?.fileList}
                                >
                                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                                        <Upload
                                            beforeUpload={() => false}
                                            maxCount={1}
                                            fileList={fileList}
                                            accept="image/png, image/jpg, image/jpeg"
                                            onChange={({ fileList }) => {
                                                form.setFieldsValue({ logo: fileList });
                                                setFileList(fileList);
                                            }}
                                            showUploadList={false}
                                        >
                                            <Button icon={<UploadOutlined />}>Upload</Button>
                                        </Upload>
                                        {fileList.length > 0 && (
                                            <DeleteOutlined
                                                onClick={() => {
                                                    setFileList([]);
                                                    form.setFieldsValue({ logo: [] });
                                                    onChange('logo', undefined);
                                                }}
                                                style={{ color: 'red', cursor: 'pointer', fontSize: 18 }}
                                                title="Remove file"
                                            />
                                        )}
                                    </div>
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="favicon"
                                    label="Favicon"
                                    valuePropName="fileList"
                                    getValueFromEvent={e => e?.fileList}
                                >
                                    <Upload
                                        maxCount={1}
                                        beforeUpload={() => false}
                                        accept="image/x-icon"
                                        fileList={fileListFav}
                                        onChange={({ fileList }) => {
                                            setFileListFav(fileList);
                                        }}
                                    >
                                        <Button icon={<UploadOutlined />}>Upload</Button>
                                    </Upload>
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="htmlTitle"
                                    label="HTML Page Title"
                                >
                                    <Input placeholder="HTML Page Title" />
                                </Form.Item>
                            </Col>
                            <Col
                                span={24}
                            >
                                <Form.Item
                                    name="noHeader"
                                    label="No Header"
                                    valuePropName="checked"
                                >
                                    <Checkbox />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.components.Layout.headerBg"
                                    label="Header Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorBgLayout"
                                    label="Background Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorPrimary"
                                    label="Primary Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorError"
                                    label="Error Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorSuccess"
                                    label="Success Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorInfo"
                                    label="Info Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorWarning"
                                    label="Warning Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorText"
                                    label="Text Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.colorTextHeading"
                                    label="Heading Color"
                                >
                                    <ColorPicker format="hex" allowClear />
                                </Form.Item>
                            </Col>
                            <Col span={24}>
                                <Form.Item
                                    name="fontLink"
                                    label="Font Family Link"
                                    rules={[{
                                        pattern: /^https:\/\/fonts.googleapis.com\/.*?family=([^:]+):/,
                                        message: 'Need a fonts.'
                                    }]}
                                    getValueFromEvent={(e) => {
                                        if (!e?.target?.value) {
                                            setFontLink(undefined);
                                            return e?.target?.value;
                                        } else {
                                            const matched = (e.target.value as string).match(/^https:\/\/fonts.googleapis.com\/.*?family=([^:]+):/);
                                            if (matched?.length && matched?.length > 1) {
                                                // const family = decodeURIComponent(matched[1].replaceAll('+', ' '));
                                                setFontLink(e.target.value);
                                            } else {
                                                setFontLink(undefined);
                                            }
                                            return e.target.value;
                                        }
                                    }}
                                >
                                    <Input
                                        placeholder="Font Family Link"
                                    />
                                </Form.Item>
                                <Form.Item
                                    name="configuration.token.fontFamily"
                                    label="Font Family Name"
                                >
                                    <Input placeholder="Font Family Name" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="configuration.token.fontSize"
                                    label="Font Size"
                                >
                                    <Input placeholder="Font Size" inputMode="numeric" suffix="px" />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Divider className="mt-1" />
                        <Row justify="end">
                            <Col>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </Col>
                        </Row>

                        <Form.Item name="configuration.components.Button.fontWeight" />
                        <Form.Item name="configuration.components.Button.paddingInline" />
                        <Form.Item name="configuration.components.Button.controlHeight" />
                        <Form.Item name="configuration.components.Button.borderRadius" />
                        <Form.Item name="configuration.token.colorTextLightSolid" />
                    </Form>
                </Col>
                <Col xs={24} sm={18}>
                    <div className="config_border">
                        <ConfigProvider key={key} theme={merge(structuredClone(defaultTheme), override.configuration)}>
                            <SharedLayout
                                logo={override.logo}
                                isTest
                                noHeader={override.noHeader}
                            >
                                <div className="ma-2">
                                    <TextDisplay text={loremIpsum.text} header={loremIpsum.header} />
                                    <Row className="mt-4 mb-4">
                                        <Col>
                                            <Button type="primary">Primary</Button>
                                        </Col>
                                        <Col>
                                            <Button className="ml-2">Secondary</Button>
                                        </Col>
                                        <Col>
                                            <Button className="ml-2" danger>Error</Button>
                                        </Col>
                                    </Row>

                                    <Row className="darkBlue pa-2 br-sm">
                                        <Col>
                                            <Button type="primary">Primary</Button>
                                        </Col>
                                        <Col>
                                            <Button className="ml-2">Secondary</Button>
                                        </Col>
                                        <Col>
                                            <Button className="ml-2" danger>Error</Button>
                                        </Col>
                                    </Row>
                                    <Alert showIcon closable className="mt-2" message="Success" type="success" />
                                    <Alert showIcon closable className="mt-2" message="Info" type="info" />
                                    <Alert showIcon closable className="mt-2" message="Warning" type="warning" />
                                    <Alert showIcon closable className="mt-2" message="Error" type="error" />
                                </div>
                            </SharedLayout>
                        </ConfigProvider>
                    </div>
                </Col>
            </Row>
        </>
    );
};

export default ThemeConfiguration;
